import moment from "moment";
import React, { FC, useState } from "react";
import { LuLock } from "react-icons/lu";
import { toast } from "react-toastify";

import {
  useGetInvoicePortalStateQuery,
  useShareInvoiceMutation,
} from "../../slices/ClientPortalSlice";
import {
  errorText,
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "./PortalModal";

/**
 * Invoice page: show the invoice in the client portal (or stop showing it).
 * Invoices have no draft/sent state, so nothing is shown until the firm says so.
 */
export const InvoicePortalShare: FC<{
  invoiceId?: string;
  invoiceNo?: number;
}> = ({ invoiceId, invoiceNo }) => {
  const { data } = useGetInvoicePortalStateQuery(invoiceId || "", {
    skip: !invoiceId,
  });
  const [share, shareState] = useShareInvoiceMutation();
  const [open, setOpen] = useState(false);
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState("");
  if (!invoiceId || !data) return null;

  const run = async (shared: boolean) => {
    const result = await share({
      invoiceId,
      shared,
      notify: shared && notify,
      message: message.trim() || undefined,
    });
    if ("error" in result)
      return toast(errorText(result.error), { type: "error" });
    setOpen(false);
    setMessage("");
    toast(
      shared
        ? result.data.emailed
          ? `Shown in the client portal and emailed to ${result.data.emailed} ${
              result.data.emailed === 1 ? "person" : "people"
            }`
          : "Shown in the client portal"
        : "No longer shown in the client portal",
      { type: "success" },
    );
  };

  return (
    <>
      <div
        className={
          "mt-3 flex items-center gap-3 rounded-[10px] border border-[#EDEFF3] bg-[#FBFBFD] px-3.5 py-3"
        }>
        <span
          className={
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#EBE6FD] text-[#6938EF]"
          }>
          <LuLock size={15} />
        </span>
        <div className={"flex min-w-0 flex-1 flex-col"}>
          <span className={"text-[13px] font-extrabold text-[#1F2433]"}>
            Client portal
          </span>
          <span className={"text-[12px] font-semibold text-[#6B7185]"}>
            {data.shared
              ? `Shown to the client since ${moment(data.sharedAt).format(
                  "MMM D",
                )}`
              : "Not shown to the client"}
          </span>
        </div>
        {data.shared ? (
          <button
            type={"button"}
            disabled={shareState.isLoading}
            onClick={() => run(false)}
            className={
              "h-8 rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[12.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA]"
            }>
            Stop showing
          </button>
        ) : (
          <button
            type={"button"}
            onClick={() => setOpen(true)}
            className={
              "h-8 rounded-[8px] bg-[#6B4EFF] px-3 text-[12.5px] font-extrabold text-white hover:bg-[#5B3DF5]"
            }>
            Show in portal
          </button>
        )}
      </div>

      {open && (
        <PortalModal
          title={`Show invoice #${invoiceNo ?? ""} in the client portal?`}
          subtitle={
            "The client can see the invoice and download its PDF. They can't pay online from the portal."
          }
          onClose={() => setOpen(false)}
          width={500}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type={"button"}
                className={modalPrimary}
                disabled={shareState.isLoading}
                onClick={() => run(true)}>
                {shareState.isLoading ? "Saving…" : "Show in portal"}
              </button>
            </>
          }>
          {data.peopleWithAccess === 0 ? (
            <span
              className={
                "rounded-[10px] bg-[#FFF8E6] px-3 py-2.5 text-[12.5px] font-medium text-[#8A5A00]"
              }>
              Nobody from this client has portal access yet. The invoice will be
              there when you invite them on the client’s Client portal tab.
            </span>
          ) : (
            <>
              <label
                className={
                  "flex items-center gap-2 text-[13px] font-semibold text-[#3A4256]"
                }>
                <input
                  type={"checkbox"}
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className={"accent-[#6B4EFF]"}
                />
                Email the client ({data.peopleWithAccess}{" "}
                {data.peopleWithAccess === 1 ? "person" : "people"} with portal
                access)
              </label>
              {notify && (
                <textarea
                  className={`${modalInput} h-20 py-2`}
                  maxLength={2000}
                  placeholder={"Message (optional)"}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              )}
            </>
          )}
        </PortalModal>
      )}
    </>
  );
};
